import React from 'react';
import { Tag, Space, Typography, Button, InputNumber, message, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Datatable from 'components/Common/dataTable';
import CustomButton from 'components/Common/ui/CustomButton';
import { useTranslation } from 'react-i18next';
import { useMatrixMarks } from '../hooks/useMatrixMarks';
import UserProfileHeader from 'components/Common/ui/UserProfileHeader';
import { useAbility } from 'config/abilityContext';
import { all_routes } from 'router/all_routes';
import CustomAlert from 'components/Common/ui/CustomAlert';
import Loader from 'components/Common/loader';

const { Text } = Typography;

type Props = { classId: string; subjectId: string; semester: string };
type RowData = any & { key: string };

function statusColor(s: any['status']) {
  if (s === 'active') return 'green';
  if (s === 'deleted') return 'red';
  if (s === 'pending') return 'yellow';
  return 'gold';
}

const makeKey = (studentId: string, examSessionId: string) =>
  `${studentId}__${examSessionId}`;

const StudentExamTypeMatrixTable: React.FC<Props> = ({ classId, subjectId, semester }) => {
  const { t } = useTranslation();
  const ability = useAbility();
  const { matrix, loading, saving, loadMatrix, saveMatrix } = useMatrixMarks();

  const [edits, setEdits] = React.useState<Record<string, number | null>>({});
  const [editingCells, setEditingCells] = React.useState<Record<string, boolean>>({});
  const hasChanges = Object.keys(edits).length > 0;

  React.useEffect(() => {
    if (!classId || !subjectId || !semester) return;
    loadMatrix(classId, subjectId, semester);
    setEdits({});
    setEditingCells({});
  }, [classId, subjectId, semester, loadMatrix]);

  const rows = React.useMemo<RowData[]>(
    () => (matrix?.students || []).map((s: any) => ({ ...s, key: s.id })),
    [matrix]
  );

  const startEdit = (markId: string, studentId: string, examSessionId: string, currentValue: number | null) => {
    const editKey = makeKey(studentId, examSessionId);
    setEditingCells(m => ({ ...m, [markId]: true }));
    setEdits(m => (m.hasOwnProperty(editKey) ? m : { ...m, [editKey]: currentValue }));
  };

  const cancelEdit = (markId: string) => {
    setEditingCells(({ [markId]: _, ...rest }) => rest);
  };

  const cancelAll = () => {
    setEdits({});
    setEditingCells({});
  };

  const saveAll = async () => {
    if (!hasChanges) return;

    const payload = Object.entries(edits).flatMap(([key, value]) => {
      const [studentId, examSessionId] = key.split('__');

      const studentMarks = matrix?.matrix?.[studentId];
      if (!studentMarks) return [];

      const sessionMarks = studentMarks[examSessionId] || [];
      const mark = sessionMarks.find((m: any) => m.examSessionId === examSessionId);

      if (!mark) return [];

      return {
        markId: mark.markId,
        mark: value == null ? null : Number(value),
      };
    }).filter(Boolean);

    try {
      await saveMatrix(payload);
      await loadMatrix(classId, subjectId, semester);
      setEdits({});
      setEditingCells({});
    } catch {
      message.error(t('save_failed'));
    }
  };

  const baseColumns: ColumnsType<RowData> = [
    {
      title: t('student'),
      dataIndex: 'fullName',
      fixed: 'left',
      width: 260,
      render: (_v, r) => (
        <UserProfileHeader
          firstName={r.firstName}
          lastName={r.lastName}
          photo={r.photo}
          subtitle={r.massarCode}
        />
      ),
      sorter: (a, b) => a.fullName.localeCompare(b.fullName),
    },
  ];

  const examSessionColumns: ColumnsType<RowData> = React.useMemo(() => {
    if (!matrix) return [];

    const examSessions = (matrix.examSessions || []).map((es: any) => ({
      id: es.id,
      name: es.name,
      label: es.label,
      examDate: es.examDate,
      examTypeId: es.examTypeId,
      examTypeName: es.examTypeName,
      examTypeSlug: es.examTypeSlug,
    }));

    const mm = matrix.matrix || {};

    return examSessions.map((es: any) => ({
      title: (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
            {es.examTypeName}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>
            {es.name}
          </div>
        </div>
      ),
      key: `col_${es.id}`,
      width: 200,
      align: 'center',
      render: (_v: any, row: any) => {
        const sessionMarks = mm[row.id]?.[es.id] || [];
        const mark = sessionMarks[0];

        if (!mark) return <span style={{ opacity: 0.5 }}>—</span>;

        const editKey = makeKey(row.id, es.id);
        const isEditing = !!editingCells[mark.markId];
        const value = editKey in edits ? edits[editKey] : mark?.mark ?? null;

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space>
              {isEditing ? (
                <>
                  <InputNumber
                    min={0}
                    max={20}
                    value={value as any}
                    onChange={(v) => {
                      setEdits((prev) => ({
                        ...prev,
                        [editKey]: v === null ? null : Number(v),
                      }));
                    }}
                    style={{ width: 90 }}
                  />
                  <Button size="small" onClick={() => cancelEdit(mark.markId)}>
                    {t('cancel')}
                  </Button>
                </>
              ) : (
                <>
                  <Tag
                    color={statusColor(mark.status)}
                    style={{ minWidth: '50px', textAlign: 'center' }}
                  >
                    {mark.mark ?? '—'}
                  </Tag>
                  {ability.can("update", "Mark") && (
                    <Button
                      size="small"
                      onClick={() => startEdit(mark.markId, row.id, es.id, mark.mark ?? null)}
                    >
                      {t('edit')}
                    </Button>
                  )}
                </>
              )}
            </Space>
          </Space>
        );
      },
    }));
  }, [matrix, edits, editingCells, t, ability]);

  const columns = React.useMemo(
    () => [...baseColumns, ...examSessionColumns],
    [baseColumns, examSessionColumns]
  );

  if (loading) {
    return <Loader />;
  }

  if (examSessionColumns.length === 0) {
    return (
      <CustomAlert
        showIcon
        message={t("mark_matrix.no_exam_types.message")}
        description={t("mark_matrix.no_exam_types.description")}
        type="warning"
        asLink
        to={all_routes.examSession}
        buttonLabel={t('create_exam_session')}
        canShowButton={ability.can("getAll", "ExamSession")}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <CustomAlert
        showIcon
        message={t("mark_matrix.no_students.message")}
        description={t("mark_matrix.no_students.description")}
        asLink
        to={all_routes.detailClass.replace(':classId', classId) + '?tab=students'}
        buttonLabel={t('add_student')}
        canShowButton={ability.can("create", "Student") && ability.can("getAll", "Class")}
      />
    );
  }

  return (
    <div>
      <div className="d-flex mb-3 justify-content-end gap-2">
        <div className="d-flex gap-2">
          <CustomButton disabled={!hasChanges || saving} onClick={saveAll}>
            <i className="ti ti-square-rounded-check me-2"></i>
            {t('save')}
          </CustomButton>
          <CustomButton disabled={!hasChanges || saving} onClick={cancelAll}>
            <i className="ti ti-square-rounded-x me-2"></i>
            {t('cancel')}
          </CustomButton>
        </div>
      </div>

      <div className="d-flex mb-3 justify-content-end gap-2">
        <Tag color={statusColor("active")}>{t("mark_matrix.status.active")}</Tag>
        <Tag color={statusColor("pending")}>{t("mark_matrix.status.pending")}</Tag>
      </div>

      <Datatable
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading || saving}
        bordered
        scroll={{
          y: "calc(100vh - 220px)",
          x: "max-content",
        }}
      />
    </div>
  );
};

export default StudentExamTypeMatrixTable;